import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import { Calendar, momentLocalizer, SlotInfo } from 'react-big-calendar'
import moment from 'moment'
import styled, { createGlobalStyle } from 'styled-components'
import { CalendarDays, CalendarRange, CalendarCheck, Sun, MoreVertical, Pencil, Trash2, Share2, Sparkles } from 'lucide-react'
import EventCreateModal from '../../molecules/Dashboard/EventCalendar/CreateEventModal'
import { useAppDispatch, useAppSelector } from '../../../Redux/Hooks'
import { getAllMembers } from '../../../Redux/ApiCalls/Dashboard/MembersAPI'
import { getAllEventsAPI, getEventNamesAPI, getEventSchedulingStatsAPI } from '../../../Redux/ApiCalls/Dashboard/EventAPI'
import LoadingDots from '../../atoms/Utlis/LoadinDots'
import { EventType } from '../../../Data/event.dto'
import { clearCurrentEvent, clearError, setCurrentEvent } from '../../../Redux/Slice/Dashboard/EventSlice'
import { showErrorToast } from '../../atoms/Utlis/Toast'
import { device } from '../../atoms/Utlis/media'
import PermissionGate from '../../atoms/Utlis/PermissionGate'
import { usePermissions } from '../../../hooks/usePermissions'

const localizer = momentLocalizer(moment)
interface EventData {
    eventName: string
    dateTime: Date
    members: string[]
}


const LoaderContainer = styled.div`
width:100%;
height:100%;
display:flex;
align-items: center;
justify-content: center;
`;

const PageContainer = styled.div`
  width: 100%;
  padding: 24px;
  font-family: Urbanist, sans-serif;
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex-shrink: 0;
  box-sizing: border-box;
  @media ${device.tablet} { padding: 16px; }
`;

const Hero = styled.div`
  position: relative;
  overflow: visible;
  flex-shrink: 0;
  border-radius: 20px;
  padding: 24px 26px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.16) 0%,
    rgba(206, 86, 176, 0.12) 55%,
    rgba(53, 183, 200, 0.08) 100%
  );
  border: 1px solid rgba(201, 77, 170, 0.12);
  box-shadow: 0 8px 28px rgba(201, 77, 170, 0.08);
`;

const HeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -80px;
  right: -4%;
  width: 240px;
  height: 240px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 201, 81, 0.32) 0%, transparent 70%);
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
`;

const HeroBadge = styled.span`
  display: inline-flex;
  width: fit-content;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #c94daa;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  line-height: 1.25;
  padding: 2px 0;
`;

const SubText = styled.p`
  margin: 0px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.62);
  max-width: 560px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  flex-shrink: 0;
  @media ${device.laptop} { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const WalletHero = styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  color: #fff;
  background: linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%);
  box-shadow: 0 8px 22px rgba(201, 77, 170, 0.22);
  text-align: left;
  &::after {
    content: "";
    position: absolute;
    right: -20px;
    bottom: -24px;
    width: 90px;
    height: 90px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.12);
  }
`;

const WalletIconWrap = styled.div`
  position: relative;
  z-index: 1;
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.22);
`;

const WalletHeroBody = styled.div`
  position: relative;
  z-index: 1;
  min-width: 0;
`;

const WalletHeroValue = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #fff;
  line-height: 1.1;
`;

const WalletHeroLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.88);
  margin-top: 2px;
`;

const StatCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 6px 18px rgba(23, 23, 23, 0.04);
`;

const StatIcon = styled.div<{ $tone: string }>`
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${(p) => p.$tone};
  background: ${(p) => `${p.$tone}18`};
`;

const StatBody = styled.div`
  min-width: 0;
`;

const StatValue = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #171717;
  line-height: 1.1;
`;

const StatLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.5);
  margin-top: 2px;
`;

const CalendarPanel = styled.div`
  width: 100%;
  flex: 1;
  background: #ffffff;
  border: 1px solid rgba(23, 23, 23, 0.06);
  border-radius: 20px;
  box-shadow: 0 10px 32px rgba(23, 23, 23, 0.06);
  overflow: hidden;
  padding: 20px 22px 22px;
  min-height: 720px;
`;

const CalenderConatiner = styled.div`
  height: 680px;
  padding: 0;
`;

const GlobalCalendarStyle = createGlobalStyle`
  .rbc-toolbar {
    margin-bottom: 16px;
    gap: 10px;
  }

  .rbc-toolbar .rbc-toolbar-label {
    font-family: Urbanist, sans-serif;
    font-size: 18px;
    font-weight: 800;
    letter-spacing: 0.02em;
    background: linear-gradient(135deg, #ffc951 0%, #c94daa 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .rbc-toolbar button {
    border-radius: 10px !important;
    border: 1px solid rgba(23, 23, 23, 0.1) !important;
    font-family: Montserrat, sans-serif;
    font-size: 12px;
    font-weight: 600;
    padding: 6px 14px !important;
    transition: all 0.15s ease;
  }

  .rbc-toolbar button:hover,
  .rbc-toolbar button.rbc-active {
    background: linear-gradient(144.01deg, rgba(255, 201, 81, 0.15) 0%, rgba(206, 86, 176, 0.15) 100%) !important;
    border-color: rgba(201, 77, 170, 0.25) !important;
    color: #c94daa !important;
    box-shadow: none !important;
  }

  .rbc-header {
    font-family: Montserrat, sans-serif;
    font-size: 12px;
    font-weight: 700;
    color: rgba(23, 23, 23, 0.55);
    padding: 10px 0;
    border-bottom: 1px solid rgba(23, 23, 23, 0.06) !important;
  }

  .rbc-month-view,
  .rbc-time-view {
    border-radius: 14px;
    border: 1px solid rgba(23, 23, 23, 0.06) !important;
    overflow: hidden;
  }

  .rbc-day-bg + .rbc-day-bg,
  .rbc-month-row + .rbc-month-row {
    border-color: rgba(23, 23, 23, 0.06) !important;
  }

  .rbc-off-range-bg {
    background: rgba(23, 23, 23, 0.02) !important;
  }

  .rbc-today {
    background: rgba(201, 77, 170, 0.06) !important;
  }

  .rbc-event {
    border-radius: 8px !important;
    border: none !important;
    padding: 2px 6px !important;
    font-family: Montserrat, sans-serif;
    font-size: 12px;
    font-weight: 600;
  }
`;
/* ---------- NEW: 3-dot menu styled components ---------- */

const EventContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  gap: 6px;
`;

const EventTitle = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
`;

const DotsButton = styled.button`
  background: transparent;
  border: none;
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: inherit;
  border-radius: 4px;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }
`;

const EventMenuWrapper = styled.div`
  position: relative;
`;

// NOTE: position is now controlled inline (fixed, top/left) since this
// dropdown is rendered through a Portal — see CalendarEventItem below.
const EventDropdown = styled.div`
  position: fixed;
  background: #ffffff;
  border-radius: 10px;
  border: 1px solid rgba(15, 23, 42, 0.1);
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.18);
  z-index: 10000;
  min-width: 150px;
  overflow: hidden;
`;

const EventMenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 14px;
  border: none;
  background: transparent;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #171717;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: rgba(15, 23, 42, 0.06);
  }

  &.delete {
    color: #dc2626;
  }
`;

/* ---------- NEW: Custom event renderer for react-big-calendar ---------- */

interface CalendarEventItemProps {
    event: any
    onUpdate: (event: any) => void
    onDelete: (event: any) => void
    onShare: (event: any) => void
}

const CalendarEventItem: React.FC<CalendarEventItemProps> = ({ event, onUpdate, onDelete, onShare }) => {
    const [menuOpen, setMenuOpen] = useState(false)
    const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
    const wrapperRef = useRef<HTMLDivElement>(null)
    const buttonRef = useRef<HTMLButtonElement>(null)
    const { hasPermission } = usePermissions()
    const canUpdate = hasPermission('events.update')
    const canDelete = hasPermission('events.delete')
    const hasAnyAction = canUpdate || canDelete

    // Close on click outside (checks both the trigger wrapper AND the portal-rendered menu)
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node
            const clickedInsideTrigger = wrapperRef.current?.contains(target)
            const clickedInsideMenu = (target as HTMLElement)?.closest?.('[data-event-menu-portal="true"]')
            if (!clickedInsideTrigger && !clickedInsideMenu) {
                setMenuOpen(false)
            }
        }
        if (menuOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        }
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [menuOpen])

    // Close the menu on scroll/resize so it never floats away from the button
    useEffect(() => {
        if (!menuOpen) return
        const close = () => setMenuOpen(false)
        window.addEventListener('scroll', close, true)
        window.addEventListener('resize', close)
        return () => {
            window.removeEventListener('scroll', close, true)
            window.removeEventListener('resize', close)
        }
    }, [menuOpen])

    const openMenu = () => {
        const rect = buttonRef.current?.getBoundingClientRect()
        if (rect) {
            setMenuPos({
                top: rect.bottom + 4,
                left: Math.max(8, rect.right - 150), // 150 = dropdown min-width, align right edges
            })
        }
        setMenuOpen((pre) => !pre)
    }

    return (
        <EventContent>
            <EventTitle title={event.title}>{event.title}</EventTitle>
            <EventMenuWrapper ref={wrapperRef}>
                <DotsButton
                    ref={buttonRef}
                    // react-big-calendar starts its own event-selection logic on mousedown,
                    // so we must stop it here too, not just on click
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                        openMenu()
                    }}
                >
                    <MoreVertical size={16} />
                </DotsButton>
                {menuOpen &&
                    ReactDOM.createPortal(
                        <EventDropdown
                            data-event-menu-portal="true"
                            style={{ top: menuPos.top, left: menuPos.left }}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {canUpdate && (
                                <EventMenuItem
                                    onClick={() => {
                                        setMenuOpen(false)
                                        onUpdate(event)
                                    }}
                                >
                                    <Pencil size={14} /> Update
                                </EventMenuItem>
                            )}
                            {canDelete && (
                                <EventMenuItem
                                    className="delete"
                                    onClick={() => {
                                        setMenuOpen(false)
                                        onDelete(event)
                                    }}
                                >
                                    <Trash2 size={14} /> Delete
                                </EventMenuItem>
                            )}
                            <EventMenuItem
                                onClick={() => {
                                    setMenuOpen(false)
                                    onShare(event)
                                }}
                            >
                                <Share2 size={14} /> Share
                            </EventMenuItem>
                        </EventDropdown>,
                        document.body
                    )}
            </EventMenuWrapper>
        </EventContent>
    )
}

const EventCalendar: React.FC = () => {
    const [modalOpen, setModalOpen] = useState(false)
    const [selectedSlot, setSelectedSlot] = useState<SlotInfo | null>(null)
    const [currentEvents, setCurrentEvents] = useState<any[]>([]);
    const [shareDirectly, setShareDirectly] = useState(false) // NEW: tells modal to auto-open WhatsApp share popup
    const { members, isMemberFetched } = useAppSelector((state) => state.member)
    const { selectedBranch } = useAppSelector((state) => state.auth)
    const [currentQuarter, setCurrentQuarter] = useState({
        start: new Date(new Date().getFullYear(), Math.floor(new Date().getMonth() / 3) * 3, 1),
        end: new Date(new Date().getFullYear(), Math.floor(new Date().getMonth() / 3) * 3 + 3, 0),
    });
    const { loading, Events, isError, isEventUpdate, error, success, event_scheduling } = useAppSelector(
        (state) => state.event,
    )
    const dispatch = useAppDispatch()
    useEffect(() => {
        const timeout = setTimeout(() => {
            fetchAndSetEvents(currentQuarter.start, currentQuarter.end);
        }, 700)
        return () => {
            clearTimeout(timeout);
        }
    }, [currentQuarter, selectedBranch]);

    useEffect(() => {
        if (!isMemberFetched) {
            dispatch(getAllMembers())
        }
    }, [dispatch, isMemberFetched])

    useEffect(() => {
        dispatch(getEventNamesAPI())
    }, [dispatch])

    useEffect(() => {
        const startDate = `${currentQuarter.start.getFullYear()}-${(currentQuarter.start.getMonth() + 1).toString().padStart(2, '0')}-${currentQuarter.start.getDate().toString().padStart(2, '0')}`;
        const endDate = `${currentQuarter.end.getFullYear()}-${(currentQuarter.end.getMonth() + 1).toString().padStart(2, '0')}-${currentQuarter.end.getDate().toString().padStart(2, '0')}`;
        dispatch(getEventSchedulingStatsAPI({ start_date: startDate, end_date: endDate, branch_id: selectedBranch?.id }));
    }, [dispatch, currentQuarter.start, currentQuarter.end, selectedBranch])

    useEffect(() => {

        if (isEventUpdate) {
            let startyear = currentQuarter.start.getFullYear();
            let startmonth = (currentQuarter.start.getMonth() + 1).toString().padStart(2, '0'); // Months are zero-based
            let startday = currentQuarter.start.getDate().toString().padStart(2, '0');

            // Format the date into 'yyyy-mm-dd'
            let startDate = `${startyear}-${startmonth}-${startday}`;

            let endYear = currentQuarter.end.getFullYear();
            let endMonth = (currentQuarter.end.getMonth() + 1).toString().padStart(2, '0'); // Months are zero-based
            let endDay = currentQuarter.end.getDate().toString().padStart(2, '0');

            // Format the date into 'yyyy-mm-dd'
            let endDate = `${endYear}-${endMonth}-${endDay}`;

            // console.log(currentEvents);
            dispatch(getAllEventsAPI({ start_date: startDate, end_date: endDate, branch_id: selectedBranch?.id }))
        }
        return () => {
            dispatch(clearError())
        }
    }, [dispatch, isEventUpdate, isError, selectedBranch])
    useEffect(() => {
        if (Events && Events.length > 0) {
            let newEvents = Events.map(event => {
                let newEvent = {
                    sub_events: event.sub_events,
                    title: event.title,
                    members: event.members,
                    start: new Date(event.start),
                    end: new Date(event.end),
                    id: event.id,
                    branch_id: event.branch_id,
                    branch_name: event.branch_name,
                    customer: event.customer,
                    customer_name: event.customer_name ?? (event.customer as any)?.name ?? null,
                    customer_country_code: event.customer_country_code ?? (event.customer as any)?.country_code ?? '+91',
                    customer_phone: event.customer_phone ?? (event.customer as any)?.phone ?? null,
                    customer_email: event.customer_email ?? (event.customer as any)?.email ?? null,
                }
                return newEvent

            })
            setCurrentEvents(newEvents);
        } else {
            setCurrentEvents([]);
        }
    }, [Events])

    const fetchAndSetEvents = async (start: Date, end: Date) => {
        // const newEvents = await fetchEventsForQuarter(start, end);
        // setEvents(newEvents);
        let startyear = start.getFullYear();
        let startmonth = (start.getMonth() + 1).toString().padStart(2, '0'); // Months are zero-based
        let startday = start.getDate().toString().padStart(2, '0');

        // Format the date into 'yyyy-mm-dd'
        let startDate = `${startyear}-${startmonth}-${startday}`;

        let endYear = end.getFullYear();
        let endMonth = (end.getMonth() + 1).toString().padStart(2, '0'); // Months are zero-based
        let endDay = end.getDate().toString().padStart(2, '0');

        // Format the date into 'yyyy-mm-dd'
        let endDate = `${endYear}-${endMonth}-${endDay}`;

        // console.log(currentEvents);
        dispatch(getAllEventsAPI({ start_date: startDate, end_date: endDate, branch_id: selectedBranch?.id }))
        // // console.log(start.getDate(), end.getDate("yyyy-MM-dd"));
    };

    const handleNavigate = (date: Date, view: string, action: string) => {
        const newStart = new Date(date.getFullYear(), Math.floor(date.getMonth() / 3) * 3, 1);
        const newEnd = new Date(date.getFullYear(), Math.floor(date.getMonth() / 3) * 3 + 3, 0);
        if (newStart.getTime() !== currentQuarter.start.getTime() || newEnd.getTime() !== currentQuarter.end.getTime()) {
            setCurrentQuarter({ start: newStart, end: newEnd });
        }
        // setCurrentQuarter({ start, end });
    };

    const handleSelectSlot = (slotInfo: SlotInfo) => {
        const today = moment().startOf('day');
        const selectedDate = moment(slotInfo.start).startOf('day');
        if (selectedDate.isBefore(today)) {
            return;
        }
        setSelectedSlot(slotInfo)
        setModalOpen(true)
    }

    // NEW: centralized modal-open logic, shared by clicking an event and by the 3-dot menu
    const openEventModal = (data: any, shareMode: boolean = false) => {
        const eventObje: EventType = {
            id: data.id,
            start: data.start.toString(),
            end: data.end.toString(),
            sub_events: data.sub_events,
            members: data.members,
            title: data.title,
            branch_id: data.branch_id,
            branch_name: data.branch_name,
            customer: data.customer,
            customer_name: data.customer_name ?? data.customer?.name ?? '',
            customer_country_code: data.customer_country_code ?? data.customer?.country_code ?? '+91',
            customer_phone: data.customer_phone ?? data.customer?.phone ?? '',
            customer_email: data.customer_email ?? data.customer?.email ?? '',
        }

        dispatch(setCurrentEvent(eventObje));
        setShareDirectly(shareMode)
        setModalOpen(true);
    }

    const handleSelectedEvent = (data: any) => {
        openEventModal(data, false)
    }

    // NEW: 3-dot menu handlers
    const handleMenuUpdate = (data: any) => openEventModal(data, false)
    const handleMenuDelete = (data: any) => openEventModal(data, false)
    const handleMenuShare = (data: any) => openEventModal(data, true)

    const handleCloseModal = () => {
        setModalOpen(false)
        setSelectedSlot(null)
        setShareDirectly(false) // NEW: reset share-mode flag
        dispatch(clearCurrentEvent());
    }

    const handleSubmitEvent = (eventData: EventData) => {
        // Here you would handle submitting the event data to your backend or state management
        // console.log('Submitted event data:', eventData)
        // For demo, just close the modal
        handleCloseModal()
    }

    return (
        <PageContainer>
            <GlobalCalendarStyle />

            <Hero>
                <HeroGlow />
                <HeroContent>
                    <div className="flex flex-col">
                        <HeroBadge>
                            <Sparkles size={13} />
                            Studio calendar
                        </HeroBadge>
                        <Title className="linear-text">Event Scheduling</Title>
                        <SubText>Schedule and manage your calendar events — click a slot to create or an event to edit.</SubText>
                    </div>
                </HeroContent>
            </Hero>

            {event_scheduling && (
                <StatsGrid>
                    <WalletHero>
                        <WalletIconWrap>
                            <CalendarDays size={16} />
                        </WalletIconWrap>
                        <WalletHeroBody>
                            <WalletHeroValue>{event_scheduling.total_events_scheduled}</WalletHeroValue>
                            <WalletHeroLabel>Total scheduled</WalletHeroLabel>
                        </WalletHeroBody>
                    </WalletHero>

                    <StatCard>
                        <StatIcon $tone="#f09a3e">
                            <CalendarRange size={16} />
                        </StatIcon>
                        <StatBody>
                            <StatValue>{event_scheduling.events_scheduled_this_month}</StatValue>
                            <StatLabel>This month</StatLabel>
                        </StatBody>
                    </StatCard>

                    <StatCard>
                        <StatIcon $tone="#7c3aed">
                            <CalendarCheck size={16} />
                        </StatIcon>
                        <StatBody>
                            <StatValue>{event_scheduling.events_scheduled_this_week}</StatValue>
                            <StatLabel>This week</StatLabel>
                        </StatBody>
                    </StatCard>

                    <StatCard>
                        <StatIcon $tone="#31a66a">
                            <Sun size={16} />
                        </StatIcon>
                        <StatBody>
                            <StatValue>{event_scheduling.events_scheduled_today}</StatValue>
                            <StatLabel>Today</StatLabel>
                        </StatBody>
                    </StatCard>
                </StatsGrid>
            )}

            <CalendarPanel>
                <CalenderConatiner>
                    <Calendar
                        localizer={localizer}
                        events={currentEvents}
                        views={{ month: true, week: true, day: true }}
                        onSelectSlot={handleSelectSlot}
                        selectable={true}
                        defaultView="month"
                        step={15}
                        timeslots={4}
                        onSelectEvent={handleSelectedEvent}
                        onNavigate={handleNavigate}
                        components={{
                            event: (props: any) => (
                                <CalendarEventItem
                                    event={props.event}
                                    onUpdate={handleMenuUpdate}
                                    onDelete={handleMenuDelete}
                                    onShare={handleMenuShare}
                                />
                            ),
                        }}
                    />
                </CalenderConatiner>
            </CalendarPanel>

            <EventCreateModal
                isOpen={modalOpen}
                onClose={handleCloseModal}
                onSubmit={handleSubmitEvent}
                selectedSlot={selectedSlot}
                initialShareOpen={shareDirectly}
            />
        </PageContainer>
    )
}

export default EventCalendar